import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import { getInviteAndAssignPeopleOption } from '@atlassian/jira-invite-and-assign/src/common/utils.tsx';
import {
	fireUIAnalytics,
	useAnalyticsEvents,
	type AnalyticsAttributes,
	type Attributes,
} from '@atlassian/jira-product-analytics-bridge';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { useUserSubscriber } from '@atlassian/jira-user-services/src/main.tsx';
import { AUTOMATIC_ID } from '../../common/constants.tsx';
import type { UserValue, User, UserOption } from '../../common/types.tsx';
import {
	getUserOptionFromUser,
	getAutomaticUserOption,
	getUnassignedUser,
	getInvitePeopleOption,
} from '../../common/utils.tsx';
import messages from '../../messages.tsx';

export const fireInviteItemRendererEvent = ({
	createAnalyticsEvent,
	suggestionsCount,
	isAdmin,
	isInviteAndAssignFromBoard = false,
}: {
	createAnalyticsEvent?: ((arg1: AnalyticsAttributes) => UIAnalyticsEvent) | null;
	suggestionsCount: number;
	isAdmin: boolean;
	isInviteAndAssignFromBoard?: boolean;
}) => {
	if (createAnalyticsEvent === undefined || createAnalyticsEvent === null) {
		return;
	}

	const action = 'rendered';
	const actionSubject = 'inviteItem';

	const event = createAnalyticsEvent({
		action,
		actionSubject,
	});

	const attrs: Attributes = {
		fieldId: 'assignee',
		userRole: isAdmin ? 'admin' : 'basic',
		suggestionsCount,
		source: 'assigneeField',
		...(isInviteAndAssignFromBoard && { isInviteAndAssignFromBoard: true }),
	};
	fireUIAnalytics(event, `${actionSubject} ${action}`, attrs);
};

export const useAssigneeOptions = ({
	value,
	users,
	intl,
	shouldPreloadAssignToMe,
	search,
	enablePeopleInvite,
	enableAutomaticOption,
	suggestedEmailDomain,
	isEligibleForInviteAndAssign,
	shouldHideUnassigned,
	shouldShowInviteOnOpen,
}: {
	value?: UserValue;
	users: User[];
	intl: IntlShape;
	shouldPreloadAssignToMe: boolean;
	search?: string;
	enablePeopleInvite?: boolean;
	enableAutomaticOption?: boolean;
	suggestedEmailDomain?: string;
	isEligibleForInviteAndAssign?: boolean;
	shouldHideUnassigned?: boolean;
	shouldShowInviteOnOpen?: boolean;
}): [
	UserOption,
	UserOption[],
	{
		userOptionsCount: number;
	},
] => {
	const isAdmin = useIsAdmin();
	const unassigned = getUnassignedUser(intl);
	const automaticOption = getAutomaticUserOption(intl);
	const valueOption: UserOption = getUserOptionFromUser(value || unassigned);
	const [{ data: loggedInUser }] = useUserSubscriber();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const loggedInUserOption = loggedInUser
		? {
				...getUserOptionFromUser(loggedInUser),
				publicName: intl.formatMessage(messages.assignToMe),
			}
		: null;

	const isInvalidSearch = search === undefined || search === '';

	// show unassigned option if value is defined and no search or unassigned display name match the search
	const showUnassigned =
		!shouldHideUnassigned &&
		(isInvalidSearch
			? Boolean(value?.accountId)
			: unassigned.displayName.toLowerCase().startsWith((search || '').toLowerCase()));

	let showAutomaticOption = false;

	if (enableAutomaticOption === true) {
		showAutomaticOption = isInvalidSearch
			? Boolean(value?.accountId !== AUTOMATIC_ID)
			: automaticOption.displayName.toLowerCase().startsWith((search || '').toLowerCase());
	}

	const isLoggedInUserCurrentValue =
		loggedInUserOption !== null && valueOption.id === loggedInUserOption.id;

	const isLoggedInUserMatchSearch = users.some(
		(user: User) => loggedInUserOption !== null && user.accountId === loggedInUserOption.id,
	);
	const showAssignToMeOnInit: boolean =
		!isLoggedInUserCurrentValue && (shouldPreloadAssignToMe || isLoggedInUserMatchSearch);
	const showAssignToMe: boolean = isInvalidSearch
		? showAssignToMeOnInit
		: isLoggedInUserMatchSearch;

	const userOptions: UserOption[] = users
		.filter(
			(user: User) =>
				// remove selected user (value) from users list when search is not defined
				(!isInvalidSearch || user.accountId !== valueOption.id) &&
				// remove logged in user (is already added in 2nd position above)
				!(loggedInUserOption !== null && user.accountId === loggedInUserOption.id),
		)
		.map(getUserOptionFromUser);

	const invitePeopleOptions: Array<UserOption> = [];

	if (enablePeopleInvite === true && shouldShowInviteOnOpen) {
		if (isEligibleForInviteAndAssign && search) {
			invitePeopleOptions.push(
				getInviteAndAssignPeopleOption(intl.formatMessage, search, suggestedEmailDomain),
			);
		} else {
			invitePeopleOptions.push(getUserOptionFromUser(getInvitePeopleOption(intl, isAdmin)));
		}
	} else if (enablePeopleInvite === true && !isInvalidSearch && userOptions.length < 3) {
		if (isEligibleForInviteAndAssign) {
			invitePeopleOptions.push(
				getInviteAndAssignPeopleOption(intl.formatMessage, search, suggestedEmailDomain),
			);
			fireInviteItemRendererEvent({
				createAnalyticsEvent,
				suggestionsCount: userOptions.length,
				isAdmin,
				isInviteAndAssignFromBoard: true,
			});
		} else {
			fireInviteItemRendererEvent({
				createAnalyticsEvent,
				suggestionsCount: userOptions.length,
				isAdmin,
			});
			invitePeopleOptions.push(getUserOptionFromUser(getInvitePeopleOption(intl, isAdmin)));
		}
	}

	const options: UserOption[] = [
		...(showUnassigned ? [getUserOptionFromUser(unassigned)] : []),
		...(showAutomaticOption ? [getUserOptionFromUser(automaticOption)] : []),
		...(showAssignToMe && loggedInUserOption !== null ? [loggedInUserOption] : []),
		...userOptions,
		...invitePeopleOptions,
	];

	return [
		valueOption,
		options,
		{
			userOptionsCount: userOptions.length,
		},
	];
};
